import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  DataTablesList,
  EmailVerb,
  EmailVerbEnum,
  EmailVerbTypeInfo,
  EmailVerbWithTags,
  EmailVerbWrite,
  EmailVerbsWithTags,
} from '@digilize/shared/definitions/src';

@Injectable({
  providedIn: 'root',
})
export class EmailVerbsApiService {
  readonly BASE_EMAIL_VERBS_URL_V1 = '/api/v1.0/email-verbs';
  readonly BASE_EMAIL_VERBS_URL_V2 = '/api/v2.0/email-verbs';
  readonly BASE_COMPANIES_URL_V1 = '/api/v1.0/companies';
  constructor(private http: HttpClient) {}

  /** Platform default verb + insertable tags for a type. Type ids are plain strings, not only the enum. */
  getEmailVerb(id: EmailVerbEnum | string) {
    return this.http.get<EmailVerbWithTags>(`${this.BASE_EMAIL_VERBS_URL_V1}/${id}`);
  }

  /** Every registered verb type, sorted by category (nulls last) then name. */
  getTypes() {
    return this.http.get<EmailVerbTypeInfo[]>(`${this.BASE_EMAIL_VERBS_URL_V1}/types`);
  }

  getEmailVerbs(params: EmailVerbsParams) {
    return this.http.get<EmailVerbsWithTags>(`${this.BASE_EMAIL_VERBS_URL_V2}`, { params: { ...params } });
  }

  matchSingle(params: MatchSingleParams) {
    return this.http.get<EmailVerbWithTags>(`${this.BASE_EMAIL_VERBS_URL_V2}/match`, { params: { ...params } });
  }

  getCompanyVerbs(companyId: string, params?: CompanyVerbsParams) {
    return this.http.get<DataTablesList<EmailVerb>>(this.companyVerbsUrl(companyId), { params: { ...params } });
  }

  getCompanyVerb(companyId: string, verbId: string) {
    return this.http.get<EmailVerbWithTags>(`${this.companyVerbsUrl(companyId)}/${verbId}`);
  }

  addCompanyVerb(companyId: string, payload: EmailVerbWrite) {
    return this.http.post<EmailVerb>(this.companyVerbsUrl(companyId), payload);
  }

  updateCompanyVerb(companyId: string, verbId: string, payload: EmailVerbWrite) {
    return this.http.put<EmailVerb>(`${this.companyVerbsUrl(companyId)}/${verbId}`, payload);
  }

  deleteCompanyVerb(companyId: string, verbId: string) {
    return this.http.delete(`${this.companyVerbsUrl(companyId)}/${verbId}`);
  }

  /** Renders the posted (unsaved) model as the final email HTML for a sandboxed iframe. */
  previewCompanyVerb(companyId: string, payload: EmailVerbWrite, language: string) {
    return this.http.post<{ html: string }>(`${this.companyVerbsUrl(companyId)}/preview`, payload, {
      params: { language },
    });
  }

  private companyVerbsUrl(companyId: string) {
    return `${this.BASE_COMPANIES_URL_V1}/${companyId}/email-verbs`;
  }
}

export interface CompanyVerbsParams {
  order_column_name?: string;
  order_column_descending?: boolean;
}

export interface MatchSingleParams {
  company?: string;
  research?: string;
  researchGroup?: string;
}

export interface EmailVerbsParams extends MatchSingleParams {
  type: EmailVerbEnum[];
}
